/**
 * Blake's own dialogs, in the page — instead of the browser's alert /
 * confirm / prompt. If someone ticks the browser's "don't let this page
 * create more dialogs", those silently stop working and every button that
 * asks first looks dead (Joe, 15 Oct 2026). These can't be switched off.
 *
 *   await uiAlert('Saved.')
 *   if (!(await uiConfirm('Delete it?', { okLabel: 'Delete', danger: true }))) return;
 *   const reason = await uiPrompt('Reason?', '')          // null if cancelled
 *   const pick = await uiChoose('Cancel the run?', [{ value: 'undo', label: 'Cancel and undo', danger: true }, …])
 *
 * Plain DOM, no React provider needed, so they work from anywhere.
 */

function el(tag, style, text) {
  const e = document.createElement(tag);
  if (style) Object.assign(e.style, style);
  if (text != null) e.textContent = text;
  return e;
}

function open({ title, message, input, buttons }) {
  return new Promise((resolve) => {
    const prevFocus = document.activeElement;
    const back = el('div', { position: 'fixed', inset: '0', background: 'rgba(0,0,0,.45)', zIndex: '3000', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' });
    const box = el('div', { background: 'var(--surface, #fff)', color: 'var(--text, #111)', borderRadius: '12px', maxWidth: '480px', width: '100%', boxShadow: '0 20px 50px rgba(0,0,0,.25)', padding: '20px 22px', fontFamily: 'inherit' });
    box.setAttribute('role', 'dialog');
    box.setAttribute('aria-modal', 'true');
    if (title) box.appendChild(el('div', { fontSize: '16px', fontWeight: '700', marginBottom: '8px' }, title));
    if (message) box.appendChild(el('div', { fontSize: '14px', lineHeight: '1.5', whiteSpace: 'pre-line', color: 'var(--text, #111)' }, message));
    let field = null;
    if (input) {
      field = el(input.multiline ? 'textarea' : 'input', { width: '100%', boxSizing: 'border-box', marginTop: '12px', padding: '9px 12px', borderRadius: '8px', border: '1px solid var(--border, #ccc)', background: 'var(--surface-2, #f6f6f4)', color: 'var(--text, #111)', fontSize: '14px', fontFamily: 'inherit' });
      field.value = input.value ?? '';
      if (input.placeholder) field.placeholder = input.placeholder;
      box.appendChild(field);
    }
    const row = el('div', { display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: '18px' });
    const done = (v) => {
      document.removeEventListener('keydown', onKey, true);
      back.remove();
      if (prevFocus && prevFocus.focus) try { prevFocus.focus(); } catch { /* gone */ }
      resolve(v);
    };
    const cancelValue = buttons.find((b) => b.cancel)?.value ?? null;
    buttons.forEach((b) => {
      const btn = el('button', {
        padding: '8px 16px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
        border: b.primary || b.danger ? 'none' : '1px solid var(--border, #ccc)',
        background: b.danger ? '#dc2626' : b.primary ? 'var(--pld-green, #22391f)' : 'var(--surface-2, #f3f3f0)',
        color: b.danger || b.primary ? '#fff' : 'var(--text, #111)',
      }, b.label);
      btn.type = 'button';
      btn.onclick = () => done(b.value === '__input__' ? (field ? field.value : '') : b.value);
      row.appendChild(btn);
      if (b.focus) setTimeout(() => btn.focus(), 0);
    });
    box.appendChild(row);
    back.appendChild(box);
    back.addEventListener('mousedown', (e) => { if (e.target === back) done(cancelValue); });
    function onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); done(cancelValue); }
      if (e.key === 'Enter' && field && !input.multiline) { e.preventDefault(); done(field.value); }
    }
    document.addEventListener('keydown', onKey, true);
    document.body.appendChild(back);
    if (field) setTimeout(() => field.focus(), 0);
  });
}

export function uiAlert(message, { title } = {}) {
  return open({ title, message: String(message ?? ''), buttons: [{ label: 'OK', value: true, primary: true, focus: true, cancel: true }] });
}

export function uiConfirm(message, { title, okLabel = 'OK', cancelLabel = 'Cancel', danger = false } = {}) {
  return open({ title, message, buttons: [
    { label: cancelLabel, value: false, cancel: true },
    { label: okLabel, value: true, primary: !danger, danger, focus: true },
  ] });
}

export function uiPrompt(message, value = '', { title, okLabel = 'OK', placeholder, multiline } = {}) {
  return open({ title, message, input: { value, placeholder, multiline }, buttons: [
    { label: 'Cancel', value: null, cancel: true },
    { label: okLabel, value: '__input__', primary: true },
  ] });
}

/** Several choices; resolves to the chosen value, or null for "Back". */
export function uiChoose(message, choices, { title, backLabel = 'Back' } = {}) {
  return open({ title, message, buttons: [{ label: backLabel, value: null, cancel: true }, ...choices] });
}

/**
 * Make every plain alert() in Blake use the in-page dialog — alerts never
 * return anything, so this is safe app-wide.
 */
export function installDialogs() {
  window.alert = (m) => { uiAlert(m); };
}
